import { HostFloorPlanTable } from 'restaurantAdmin/floorPlans/apiHelpers';
import { useDefinedContext } from '@shared/hooks/useDefinedContext';
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useState,
} from 'react';
import { useReservationServiceContext } from './ReservationServiceContext';
import { convertToMergeAwareTables } from 'restaurantAdmin/floorPlans/utils';
import { Seating } from '../apiHelpers';

export interface ReservationServiceSelectedSeatingContextState {
  selectedFloorPlanTables: HostFloorPlanTable[];
  setSelectedFloorPlanTables: Dispatch<SetStateAction<HostFloorPlanTable[]>>;
  toggleSelectedFloorPlanTable: (table: HostFloorPlanTable) => void;
  clearSelectedFloorPlanTables: () => void;
}

export const ReservationServiceSelectedSeatingContext =
  createContext<ReservationServiceSelectedSeatingContextState | null>(null);

export const useReservationServiceSelectedSeatingContext =
  (): ReservationServiceSelectedSeatingContextState =>
    useDefinedContext(ReservationServiceSelectedSeatingContext);

export const ReservationServiceSelectedSeatingContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { selectedFloorPlan, selectedOccupant } =
    useReservationServiceContext();
  const [selectedFloorPlanTables, setSelectedFloorPlanTables] = useState<
    HostFloorPlanTable[]
  >([]);

  const mergeAwareFloorPlanTables = convertToMergeAwareTables(
    selectedFloorPlan?.floorPlanTables || [],
  );

  const occupantIsSeated = Boolean(selectedOccupant?.seatedTableName);

  useEffect(() => {
    if (occupantIsSeated) {
      const tablesWhereTheOccupantIsSeated =
        deriveSelectedFloorPlanTablesFromOccupant({
          seatings: selectedOccupant?.seatings,
          floorPlanTables: mergeAwareFloorPlanTables,
        });

      setSelectedFloorPlanTables(tablesWhereTheOccupantIsSeated);
    }
    // Both the occupant and floor plan/tables come in async, so we need to
    // recompute to avoid false positives / negatives.
  }, [occupantIsSeated, mergeAwareFloorPlanTables.length]);

  const toggleSelectedFloorPlanTable = (table: HostFloorPlanTable) => {
    setSelectedFloorPlanTables((prevTables) =>
      prevTables.includes(table)
        ? prevTables.filter((t) => t.id !== table.id)
        : [...prevTables, table],
    );
  };

  const clearSelectedFloorPlanTables = () => {
    setSelectedFloorPlanTables([]);
  };

  return (
    <ReservationServiceSelectedSeatingContext.Provider
      value={{
        selectedFloorPlanTables,
        setSelectedFloorPlanTables,
        toggleSelectedFloorPlanTable,
        clearSelectedFloorPlanTables,
      }}
    >
      {children}
    </ReservationServiceSelectedSeatingContext.Provider>
  );
};

const deriveSelectedFloorPlanTablesFromOccupant = ({
  floorPlanTables,
  seatings,
}: {
  floorPlanTables: HostFloorPlanTable[];
  seatings?: Seating[];
}) => {
  const occupantSeatingTableIds = (seatings || []).map(
    (seating) => seating.floorPlanTableId,
  );
  return floorPlanTables.filter((table) => {
    return occupantSeatingTableIds.includes(table.id);
  });
};
